import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const InactivityWarning = ({ open, onContinue, onLogout, warningSeconds = 15 }) => {
    const [countdown, setCountdown] = useState(warningSeconds);
    const activeRef = useRef(false);
    const onLogoutRef = useRef(onLogout);

    useEffect(() => {
        onLogoutRef.current = onLogout;
    }, [onLogout]);

    useEffect(() => {
        if (!open) {
            activeRef.current = false;
            setCountdown(warningSeconds);
            return;
        }
        activeRef.current = true;
        setCountdown(warningSeconds);

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (!activeRef.current) {
                    clearInterval(interval);
                    return warningSeconds;
                }
                if (prev <= 1) {
                    clearInterval(interval);
                    if (activeRef.current) {
                        onLogoutRef.current();
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            activeRef.current = false;
            clearInterval(interval);
        };
    }, [open, warningSeconds]);

    const progress = ((warningSeconds - countdown) / warningSeconds) * 100;

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                // Prevent closing by clicking outside or pressing escape
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    padding: '8px',
                },
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <WarningAmberIcon sx={{ fontSize: 50, color: '#4F63BE' }} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" sx={{ mb: 1, color: '#000', fontWeight: "500" }}>
                    You've been inactive for a while. Your session will expire soon.
                </Typography>

                <Typography variant="body2" sx={{ color: '#1C1C1C' }}>
                    {countdown > 1
                        ? `Logging out in ${countdown} seconds...`
                        : 'Logging out...'}
                </Typography>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
                <Button
                    onClick={onLogout}
                    variant="contained"
                    sx={{
                        minWidth: '180px',
                        borderRadius: '24px',
                        textTransform: 'none',
                        backgroundColor: '#7398C7',
                    }}
                >
                    Logout Now
                </Button>
                <Button
                    onClick={onContinue}
                    variant="contained"
                    sx={{
                        minWidth: '180px',
                        borderRadius: '24px',
                        textTransform: 'none',
                        backgroundColor: '#4F63BE',
                        '&:hover': {
                            backgroundColor: '#4F63BE',
                        },

                    }}
                >
                    Stay Logged In
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InactivityWarning;